"use client";

import { useEffect, useState } from "react";

/**
 * Decodes every image up front. Without this a mark mounts before its portrait
 * or logo is decoded, so the card fades in empty and the image lands part-way
 * through the reveal, which reads as a stutter rather than as a slow image.
 */
export function useImagesReady(sources: string[]): boolean {
  const signature = sources.join("|");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const list = signature ? signature.split("|") : [];
    if (list.length === 0) {
      setReady(true);
      return;
    }
    let cancelled = false;
    const load = (src: string) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          // decode() keeps the first paint off the main thread; a failure here
          // is not worth blocking on.
          if (typeof img.decode === "function") {
            img.decode().then(
              () => {
                resolve();
              },
              () => {
                resolve();
              },
            );
          } else {
            resolve();
          }
        };
        img.onerror = () => {
          resolve();
        };
        img.src = src;
      });
    void Promise.all(list.map(load)).then(() => {
      if (!cancelled) {
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [signature]);

  return ready;
}
